import { useState, useEffect } from "react";
import { Download, Check, ChevronDown, ChevronUp, Mail, Menu, X, Tv, Zap, Radio, Shield, Clock, Infinity, Star, ArrowDown } from "lucide-react";

/* ─── translations ─────────────────────────────────────────────────────────── */
const T = {
  en: {
    nav: { home: "Home", apps: "Apps", pricing: "Pricing", faq: "FAQ", contact: "Contact" },
    hero: {
      badge: "Android IPTV Players",
      headline: ["Stream Anything.", "Anywhere."],
      sub: "Three powerful IPTV players. One seamless experience. Available on Android.",
      cta: "Download Free",
      trial: "7-day free trial · no credit card",
    },
    features: ["7-Day Free Trial", "Android Compatible", "M3U & Xtream", "Full EPG Support", "Secure & Fast"],
    players: {
      nettv: {
        name: "NetTV", tagline: "The flagship player",
        desc: "Full EPG, multi-playlist management and smooth HLS/MPEG-TS playback. The most complete IPTV experience on Android.",
        btn: "Download NetTV",
      },
      arc: {
        name: "ArcPlayer", tagline: "Geometric precision",
        desc: "A sleek minimal player with a sharp grid layout and lightning-fast channel switching — built for clarity.",
        btn: "Download ArcPlayer",
      },
      pulse: {
        name: "PulsePlayer", tagline: "Feel the beat",
        desc: "Neon aesthetics, Netflix-style poster grid, and buttery smooth playback. Your content, amplified.",
        btn: "Download PulsePlayer",
      },
    },
    pricing: {
      title: "Simple Pricing",
      sub: "Start free. Stay forever.",
      trial_title: "Free Trial",
      trial_dur: "7 Days",
      trial_note: "Included with every download — no payment needed.",
      trial_items: ["Full app access", "All playlists supported", "EPG & catch-up", "No credit card required"],
      lic_title: "Permanent License",
      lic_price: "€4.99",
      lic_once: "one-time · lifetime",
      lic_note: "Buy once. Use forever. No recurring fees.",
      lic_items: ["Lifetime activation", "All future updates", "Multi-device", "Priority support"],
      buy: "Buy License",
    },
    steps: {
      title: "Get Started in 3 Steps",
      items: [
        { n: "01", t: "Download APK", d: "Choose your player below and tap the download button. The APK installs directly on any Android phone or TV box." },
        { n: "02", t: "Add Your Playlist", d: "Open the app and enter your M3U URL or Xtream credentials. Your channels appear instantly." },
        { n: "03", t: "Activate License", d: "After the 7-day trial, email us with your device key and pay €4.99 once for permanent access." },
      ],
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Do the players include channels?", a: "No. These are media player apps — you must supply your own IPTV playlist (M3U/Xtream). We do not sell or distribute any content." },
        { q: "How does the 7-day trial work?", a: "Every download includes a 7-day free trial with full access. No payment or sign-up is required to start." },
        { q: "How do I get a permanent license?", a: "After your trial, email info.player@gmail.com with your device key. We reply with payment instructions and activate your license within minutes." },
        { q: "Which devices are supported?", a: "All three players work on Android phones, Android TV boxes, and Android-based smart TVs (Android 5.0+)." },
        { q: "Can I install all three players?", a: "Yes! Each app is completely separate with its own package ID and license. Download whichever fits your style — or all three." },
      ],
    },
    legal: {
      title: "Legal Notice",
      body: "Our apps are designed for legal use only. You must own or have the right to access any content you stream. We do not provide playlists, channels, or any copyrighted material. By clicking Accept you confirm your use complies with all applicable laws.",
      accept: "I Understand & Accept",
    },
    footer: {
      tagline: "Premium IPTV Players for Android",
      contact: "Contact",
      rights: "All rights reserved.",
      note: "For legal use only.",
    },
  },

  fi: {
    nav: { home: "Etusivu", apps: "Sovellukset", pricing: "Hinnoittelu", faq: "UKK", contact: "Yhteystiedot" },
    hero: {
      badge: "Android IPTV-soittimet",
      headline: ["Toista mitä tahansa.", "Missä tahansa."],
      sub: "Kolme tehokasta IPTV-soitinta. Yksi saumaton kokemus. Saatavana Androidille.",
      cta: "Lataa ilmaiseksi",
      trial: "7 päivän ilmainen kokeilu · ei luottokorttia",
    },
    features: ["7 päivän ilmainen kokeilu", "Android-yhteensopiva", "M3U & Xtream", "Täysi EPG-tuki", "Turvallinen & nopea"],
    players: {
      nettv: {
        name: "NetTV", tagline: "Lippulaiva-soitin",
        desc: "Täysi EPG, monipuolinen soitinlistojen hallinta ja sujuva HLS/MPEG-TS-toisto. Kattavin IPTV-kokemus Androidilla.",
        btn: "Lataa NetTV",
      },
      arc: {
        name: "ArcPlayer", tagline: "Geometrinen tarkkuus",
        desc: "Tyylikäs minimalistinen soitin terävällä ruudukkoasettelulla ja salamannopean kanavavaihdon — suunniteltu selkeydelle.",
        btn: "Lataa ArcPlayer",
      },
      pulse: {
        name: "PulsePlayer", tagline: "Tunne rytmi",
        desc: "Neon-estetiikka, Netflix-tyylinen posteriruudukko ja sujuva toisto. Sisältösi, vahvistettuna.",
        btn: "Lataa PulsePlayer",
      },
    },
    pricing: {
      title: "Yksinkertainen hinnoittelu",
      sub: "Aloita ilmaiseksi. Jää ikuisesti.",
      trial_title: "Ilmainen kokeilu",
      trial_dur: "7 päivää",
      trial_note: "Sisältyy jokaiseen lataukseen — ei maksua.",
      trial_items: ["Täysi sovellusoikeus", "Kaikki soitinlistat", "EPG & catch-up", "Ei luottokorttia"],
      lic_title: "Pysyvä lisenssi",
      lic_price: "€4,99",
      lic_once: "kertamaksu · elinikäinen",
      lic_note: "Osta kerran. Käytä ikuisesti. Ei toistuvia maksuja.",
      lic_items: ["Elinikäinen aktivointi", "Kaikki päivitykset", "Useita laitteita", "Etätuki"],
      buy: "Osta lisenssi",
    },
    steps: {
      title: "Aloita 3 vaiheessa",
      items: [
        { n: "01", t: "Lataa APK", d: "Valitse soitin ja napauta latauspainiketta. APK asentuu suoraan Android-puhelimeen tai TV-boksiin." },
        { n: "02", t: "Lisää soitinlistasi", d: "Avaa sovellus ja syötä M3U-URL tai Xtream-tunnistetiedot. Kanavasi näkyvät heti." },
        { n: "03", t: "Aktivoi lisenssi", d: "7 päivän kokeilun jälkeen lähetä sähköposti laitetunnisteesi kanssa ja maksa €4,99 kerran pysyvästä käyttöoikeudesta." },
      ],
    },
    faq: {
      title: "Usein kysytyt kysymykset",
      items: [
        { q: "Sisältävätkö soittimet kanavia?", a: "Ei. Nämä ovat mediatoistin-sovelluksia — sinun täytyy toimittaa oma IPTV-soitinlistasi (M3U/Xtream). Emme myy tai jaa sisältöä." },
        { q: "Miten 7 päivän kokeilu toimii?", a: "Jokainen lataus sisältää 7 päivän ilmaisen kokeilun täydellä käytöllä. Ei maksua tai rekisteröitymistä tarvita." },
        { q: "Miten saan pysyvän lisenssin?", a: "Kokeilun jälkeen lähetä sähköpostia osoitteeseen info.player@gmail.com laitetunnisteesi kanssa. Aktivoimme lisenssisi €4,99 kertamaksulla." },
        { q: "Mitkä laitteet ovat tuettuja?", a: "Kaikki kolme soitinta toimivat Android-puhelimilla, Android TV -bokseissa ja Android-pohjaisissa älytelevisioissa (Android 5.0+)." },
        { q: "Voinko asentaa kaikki kolme soitinta?", a: "Kyllä! Jokainen sovellus on täysin erillinen omalla paketintunnisteella ja lisenssillä." },
      ],
    },
    legal: {
      title: "Oikeudellinen ilmoitus",
      body: "Sovelluksemme on suunniteltu vain lailliseen käyttöön. Sinulla täytyy olla oikeus kaikkeen streamaamaasi sisältöön. Emme tarjoa soitinlistoja, kanavia tai tekijänoikeudella suojattua materiaalia. Hyväksymällä vahvistat, että käyttösi noudattaa sovellettavia lakeja.",
      accept: "Ymmärrän ja hyväksyn",
    },
    footer: {
      tagline: "Premium IPTV-soittimet Androidille",
      contact: "Yhteystiedot",
      rights: "Kaikki oikeudet pidätetään.",
      note: "Vain laillinen käyttö.",
    },
  },

  ar: {
    nav: { home: "الرئيسية", apps: "التطبيقات", pricing: "الأسعار", faq: "الأسئلة", contact: "اتصل" },
    hero: {
      badge: "مشغلات IPTV لأندرويد",
      headline: ["شاهد أي شيء.", "في أي مكان."],
      sub: "ثلاثة مشغلات IPTV قوية. تجربة سلسة واحدة. متاحة على أندرويد.",
      cta: "تحميل مجاني",
      trial: "تجربة مجانية 7 أيام · بدون بطاقة ائتمان",
    },
    features: ["تجربة مجانية 7 أيام", "متوافق مع أندرويد", "M3U وXtream", "دعم EPG الكامل", "آمن وسريع"],
    players: {
      nettv: {
        name: "NetTV", tagline: "المشغل الرائد",
        desc: "EPG كامل وإدارة قوائم متعددة وتشغيل سلس HLS/MPEG-TS. أكمل تجربة IPTV على أندرويد.",
        btn: "تحميل NetTV",
      },
      arc: {
        name: "ArcPlayer", tagline: "الدقة الهندسية",
        desc: "مشغل أنيق بتخطيط هندسي حاد وتبديل سريع للقنوات — مصمم للوضوح.",
        btn: "تحميل ArcPlayer",
      },
      pulse: {
        name: "PulsePlayer", tagline: "اشعر بالإيقاع",
        desc: "جماليات نيون وشبكة بوستر على طراز Netflix وتشغيل سلس. محتواك، بقوة أكبر.",
        btn: "تحميل PulsePlayer",
      },
    },
    pricing: {
      title: "تسعير بسيط",
      sub: "ابدأ مجاناً. استمر للأبد.",
      trial_title: "تجربة مجانية",
      trial_dur: "7 أيام",
      trial_note: "مشمولة مع كل تحميل — بدون دفع.",
      trial_items: ["وصول كامل للتطبيق", "جميع قوائم التشغيل", "EPG والإعادة", "بدون بطاقة ائتمان"],
      lic_title: "ترخيص دائم",
      lic_price: "€4.99",
      lic_once: "دفعة واحدة · مدى الحياة",
      lic_note: "اشترِ مرة. استخدم للأبد. بدون رسوم متكررة.",
      lic_items: ["تفعيل مدى الحياة", "جميع التحديثات", "أجهزة متعددة", "دعم مميز"],
      buy: "شراء الترخيص",
    },
    steps: {
      title: "ابدأ في 3 خطوات",
      items: [
        { n: "01", t: "تحميل APK", d: "اختر مشغلك واضغط على زر التحميل. يتم تثبيت APK مباشرة على أي هاتف أندرويد أو TV box." },
        { n: "02", t: "أضف قائمة التشغيل", d: "افتح التطبيق وأدخل رابط M3U أو بيانات Xtream. تظهر قنواتك فوراً." },
        { n: "03", t: "فعّل الترخيص", d: "بعد التجربة المجانية، أرسل لنا بريداً إلكترونياً مع مفتاح جهازك وادفع €4.99 مرة واحدة للوصول الدائم." },
      ],
    },
    faq: {
      title: "الأسئلة الشائعة",
      items: [
        { q: "هل تتضمن المشغلات قنوات؟", a: "لا. هذه تطبيقات مشغل وسائط — يجب عليك توفير قائمة IPTV الخاصة بك (M3U/Xtream). لا نبيع أو نوزع أي محتوى." },
        { q: "كيف تعمل التجربة المجانية لمدة 7 أيام؟", a: "كل تحميل يتضمن تجربة مجانية لمدة 7 أيام مع وصول كامل. لا يلزم الدفع أو التسجيل للبدء." },
        { q: "كيف أحصل على ترخيص دائم؟", a: "بعد التجربة، أرسل بريداً إلكترونياً إلى info.player@gmail.com مع مفتاح جهازك. سنفعّل ترخيصك مقابل €4.99 دفعة واحدة." },
        { q: "ما الأجهزة المدعومة؟", a: "تعمل المشغلات الثلاثة على هواتف أندرويد وصناديق Android TV وأجهزة التلفزيون الذكية (أندرويد 5.0+)." },
        { q: "هل يمكنني تثبيت المشغلات الثلاثة؟", a: "نعم! كل تطبيق منفصل تماماً بمعرف حزمة وترخيص خاص به." },
      ],
    },
    legal: {
      title: "إشعار قانوني",
      body: "تطبيقاتنا مصممة للاستخدام القانوني فقط. يجب أن تمتلك حق الوصول إلى أي محتوى تبثه. لا نقدم قوائم تشغيل أو قنوات أو مواد محمية بحقوق الطبع والنشر. بالقبول تؤكد أن استخدامك يمتثل للقوانين المعمول بها.",
      accept: "أفهم وأقبل",
    },
    footer: {
      tagline: "مشغلات IPTV المتميزة لنظام أندرويد",
      contact: "اتصل بنا",
      rights: "جميع الحقوق محفوظة.",
      note: "للاستخدام القانوني فقط.",
    },
  },

  fr: {
    nav: { home: "Accueil", apps: "Applications", pricing: "Tarifs", faq: "FAQ", contact: "Contact" },
    hero: {
      badge: "Lecteurs IPTV Android",
      headline: ["Streamez tout.", "Partout."],
      sub: "Trois puissants lecteurs IPTV. Une expérience fluide. Disponibles sur Android.",
      cta: "Télécharger gratuitement",
      trial: "Essai gratuit 7 jours · sans carte bancaire",
    },
    features: ["Essai gratuit 7 jours", "Compatible Android", "M3U & Xtream", "Support EPG complet", "Sécurisé & rapide"],
    players: {
      nettv: {
        name: "NetTV", tagline: "Le lecteur phare",
        desc: "EPG complet, gestion multi-playlists et lecture fluide HLS/MPEG-TS. L'expérience IPTV la plus complète sur Android.",
        btn: "Télécharger NetTV",
      },
      arc: {
        name: "ArcPlayer", tagline: "Précision géométrique",
        desc: "Un lecteur élégant avec une grille nette et un changement de chaînes ultra-rapide — conçu pour la clarté.",
        btn: "Télécharger ArcPlayer",
      },
      pulse: {
        name: "PulsePlayer", tagline: "Ressentez le rythme",
        desc: "Esthétique néon, grille style Netflix et lecture ultra-fluide. Votre contenu, amplifié.",
        btn: "Télécharger PulsePlayer",
      },
    },
    pricing: {
      title: "Tarification simple",
      sub: "Commencez gratuitement. Restez pour toujours.",
      trial_title: "Essai gratuit",
      trial_dur: "7 Jours",
      trial_note: "Inclus avec chaque téléchargement — aucun paiement requis.",
      trial_items: ["Accès complet à l'app", "Toutes les playlists", "EPG & replay", "Sans carte bancaire"],
      lic_title: "Licence permanente",
      lic_price: "€4,99",
      lic_once: "paiement unique · à vie",
      lic_note: "Achetez une fois. Utilisez pour toujours. Aucun frais récurrent.",
      lic_items: ["Activation à vie", "Toutes les mises à jour", "Multi-appareils", "Support prioritaire"],
      buy: "Acheter la licence",
    },
    steps: {
      title: "Démarrez en 3 étapes",
      items: [
        { n: "01", t: "Télécharger l'APK", d: "Choisissez votre lecteur et appuyez sur télécharger. L'APK s'installe directement sur tout téléphone ou TV box Android." },
        { n: "02", t: "Ajouter votre playlist", d: "Ouvrez l'app et entrez votre URL M3U ou vos identifiants Xtream. Vos chaînes apparaissent instantanément." },
        { n: "03", t: "Activer la licence", d: "Après l'essai de 7 jours, envoyez-nous un email avec votre clé d'appareil et payez €4,99 une fois pour un accès permanent." },
      ],
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        { q: "Les lecteurs incluent-ils des chaînes ?", a: "Non. Ce sont des applications de lecteur — vous devez fournir votre propre playlist IPTV (M3U/Xtream). Nous ne vendons ni ne distribuons de contenu." },
        { q: "Comment fonctionne l'essai de 7 jours ?", a: "Chaque téléchargement inclut un essai gratuit de 7 jours avec accès complet. Aucun paiement ni inscription requis." },
        { q: "Comment obtenir une licence permanente ?", a: "Après l'essai, envoyez un email à info.player@gmail.com avec votre clé d'appareil. Nous activons votre licence pour un paiement unique de €4,99." },
        { q: "Quels appareils sont supportés ?", a: "Les trois lecteurs fonctionnent sur téléphones Android, TV boxes Android et smart TVs Android (Android 5.0+)." },
        { q: "Puis-je installer les trois lecteurs ?", a: "Oui ! Chaque app est totalement séparée avec son propre identifiant et sa propre licence." },
      ],
    },
    legal: {
      title: "Avis juridique",
      body: "Nos apps sont conçues pour un usage légal uniquement. Vous devez posséder le droit d'accéder à tout contenu diffusé. Nous ne fournissons pas de playlists, chaînes ou contenu protégé par droits d'auteur. En acceptant, vous confirmez que votre utilisation respecte toutes les lois applicables.",
      accept: "Je comprends et j'accepte",
    },
    footer: {
      tagline: "Lecteurs IPTV Premium pour Android",
      contact: "Contact",
      rights: "Tous droits réservés.",
      note: "Usage légal uniquement.",
    },
  },
};

/* ─── player config ──────────────────────────────────────────────────────────── */
const PLAYERS_CONFIG = [
  {
    id: "nettv",
    key: "nettv",
    apk: "https://watchnow.publicvm.com/media/NetTV.apk",
    accent: "#00e5ff",
    accentDim: "rgba(0,229,255,0.12)",
    accentBorder: "rgba(0,229,255,0.25)",
    glow: "0 0 40px rgba(0,229,255,0.15)",
    icon: Tv,
  },
  {
    id: "arc",
    key: "arc",
    apk: "https://watchnow.publicvm.com/media/ArcPlayer-v4.apk",
    accent: "#00bfff",
    accentDim: "rgba(0,191,255,0.12)",
    accentBorder: "rgba(0,191,255,0.25)",
    glow: "0 0 40px rgba(0,191,255,0.15)",
    icon: Radio,
  },
  {
    id: "pulse",
    key: "pulse",
    apk: "https://watchnow.publicvm.com/media/PulsePlayer-v3.apk",
    accent: "#a855f7",
    accentDim: "rgba(168,85,247,0.12)",
    accentBorder: "rgba(168,85,247,0.25)",
    glow: "0 0 40px rgba(168,85,247,0.15)",
    icon: Zap,
  },
];

const LANG_FLAGS = { en: "🇬🇧", fi: "🇫🇮", ar: "🇸🇦", fr: "🇫🇷" };
const CONTACT_EMAIL = "info.player@gmail.com";

/* ─── component ──────────────────────────────────────────────────────────────── */
export default function PlayersLanding() {
  const [lang, setLang] = useState("en");
  const [openFaq, setOpenFaq] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  const t = T[lang];
  const isRTL = lang === "ar";

  useEffect(() => {
    const stored = localStorage.getItem("tt_legal_v1");
    if (stored) setLegalAccepted(true);
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Inject Google Font
  useEffect(() => {
    const id = "tt-sora-font";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const acceptLegal = () => {
    localStorage.setItem("tt_legal_v1", "1");
    setLegalAccepted(true);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenu(false);
  };

  return (
    <div
      data-testid="players-landing"
      dir={isRTL ? "rtl" : "ltr"}
      style={{ fontFamily: "'Sora', -apple-system, sans-serif", background: "#07070f", color: "#e2e8f0", minHeight: "100vh" }}
    >
      {/* ─ CSS ─ */}
      <style>{`
        .tt-glow-cyan { text-shadow: 0 0 30px rgba(0,229,255,0.4); }
        .tt-btn-primary {
          background: linear-gradient(135deg, #00e5ff, #0080ff);
          color: #000;
          font-weight: 700;
          border-radius: 9999px;
          padding: 14px 32px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
          text-decoration: none;
          cursor: pointer;
          border: none;
          font-size: 15px;
          letter-spacing: 0.01em;
        }
        .tt-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(0,229,255,0.35); }
        .tt-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 20px;
          transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
          backdrop-filter: blur(12px);
        }
        .tt-card:hover { transform: translateY(-5px); }
        .tt-section-label {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #00e5ff;
          margin-bottom: 12px;
        }
        .tt-feature-pill {
          background: rgba(0,229,255,0.07);
          border: 1px solid rgba(0,229,255,0.18);
          border-radius: 9999px;
          padding: 6px 16px;
          font-size: 13px;
          color: #94a3b8;
          white-space: nowrap;
        }
        @keyframes tt-fadeUp {
          from { opacity:0; transform: translateY(20px); }
          to   { opacity:1; transform: translateY(0); }
        }
        .tt-anim { animation: tt-fadeUp 0.6s ease both; }
        .tt-anim-1 { animation-delay: 0.1s; }
        .tt-anim-2 { animation-delay: 0.2s; }
        .tt-anim-3 { animation-delay: 0.3s; }
        @keyframes tt-pulse-ring {
          0%   { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .tt-pulse-ring {
          position: absolute; inset: -6px; border-radius: 50%;
          border: 1px solid currentColor;
          animation: tt-pulse-ring 2s ease-out infinite;
        }
        .tt-gradient-text {
          background: linear-gradient(135deg, #00e5ff 0%, #a855f7 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .tt-noise::before {
          content: '';
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          background-size: 200px 200px;
        }
        .tt-faq-item { transition: background 0.18s; }
        .tt-faq-item:hover { background: rgba(255,255,255,0.03); }
        .tt-download-btn {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 22px; border-radius: 12px;
          font-weight: 600; font-size: 14px;
          transition: transform 0.18s, box-shadow 0.18s;
          text-decoration: none; cursor: pointer;
          border: none; width: 100%; justify-content: center;
        }
        .tt-download-btn:hover { transform: translateY(-2px); }
        .tt-step-num {
          font-size: 64px; font-weight: 800;
          line-height: 1;
          background: linear-gradient(135deg, rgba(0,229,255,0.3), rgba(168,85,247,0.3));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .tt-lang-btn {
          padding: 5px 12px; border-radius: 8px;
          font-size: 13px; font-weight: 500;
          border: 1px solid transparent;
          background: transparent;
          cursor: pointer;
          transition: all 0.15s;
          color: #94a3b8;
        }
        .tt-lang-btn.active {
          background: rgba(0,229,255,0.12);
          border-color: rgba(0,229,255,0.3);
          color: #00e5ff;
        }
        .tt-lang-btn:hover:not(.active) { background: rgba(255,255,255,0.06); color: #e2e8f0; }
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
      `}</style>

      {/* noise layer */}
      <div className="tt-noise" aria-hidden="true" />

      {/* ═══ LEGAL MODAL ══════════════════════════════════════════════════════ */}
      {!legalAccepted && (
        <div
          data-testid="legal-modal"
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
          }}
        >
          <div style={{ background: "#0f1023", border: "1px solid rgba(0,229,255,0.2)", borderRadius: 20, padding: "36px", maxWidth: 480, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <Shield size={24} color="#00e5ff" />
              <span style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{t.legal.title}</span>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: "#94a3b8", marginBottom: 28 }}>{t.legal.body}</p>
            <button
              data-testid="legal-accept-btn"
              onClick={acceptLegal}
              className="tt-btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
            >
              <Check size={16} />
              {t.legal.accept}
            </button>
          </div>
        </div>
      )}

      {/* ═══ NAVBAR ══════════════════════════════════════════════════════════ */}
      <nav
        data-testid="landing-nav"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          background: navScrolled ? "rgba(7,7,15,0.92)" : "transparent",
          backdropFilter: navScrolled ? "blur(20px)" : "none",
          borderBottom: navScrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          transition: "all 0.3s ease",
          padding: "0 24px",
          height: 64,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => scrollTo("hero")}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg,#00e5ff,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tv size={16} color="#000" />
          </div>
          <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em", color: "#e2e8f0" }}>technotech</span>
        </div>

        {/* Desktop nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 32 }} className="desktop-nav">
          {["apps", "pricing", "faq"].map((k) => (
            <button
              key={k}
              onClick={() => scrollTo(k)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#94a3b8", fontWeight: 500, padding: 0, fontFamily: "inherit" }}
              className="nav-link"
            >
              {t.nav[k]}
            </button>
          ))}
        </div>

        {/* Lang + contact */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Language switcher */}
          <div data-testid="lang-switcher" style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 4 }}>
            {["en", "fi", "ar", "fr"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`tt-lang-btn${lang === l ? " active" : ""}`}
              >
                {LANG_FLAGS[l]} {l.toUpperCase()}
              </button>
            ))}
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="nav-contact-email"
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b", textDecoration: "none", padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", marginLeft: 4 }}
          >
            <Mail size={14} />
            {t.nav.contact}
          </a>
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenu(!mobileMenu)}
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", color: "#e2e8f0", padding: 6 }}
            className="mobile-menu-btn"
            data-testid="mobile-menu-btn"
          >
            {mobileMenu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenu && (
        <div style={{ position: "fixed", top: 64, left: 0, right: 0, zIndex: 99, background: "#0f1023", borderBottom: "1px solid rgba(255,255,255,0.08)", padding: "16px 24px 24px" }}>
          {["apps", "pricing", "faq"].map((k) => (
            <button key={k} onClick={() => scrollTo(k)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: "12px 0", fontSize: 15, color: "#94a3b8", fontFamily: "inherit", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              {t.nav[k]}
            </button>
          ))}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontSize: 14, color: "#00e5ff", textDecoration: "none" }}>
            <Mail size={14} /> {CONTACT_EMAIL}
          </a>
        </div>
      )}

      {/* ═══ HERO ════════════════════════════════════════════════════════════ */}
      <section
        id="hero"
        style={{
          minHeight: "100vh",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          textAlign: "center",
          padding: "120px 24px 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* bg radial glow */}
        <div aria-hidden style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,229,255,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div aria-hidden style={{ position: "absolute", bottom: "10%", right: "15%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 720, position: "relative", zIndex: 1 }}>
          {/* Badge */}
          <div className="tt-anim" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.2)", borderRadius: 9999, padding: "6px 18px", marginBottom: 32 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#00e5ff" }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: "#00e5ff" }}>{t.hero.badge}</span>
          </div>

          {/* Headline */}
          <h1 className="tt-anim tt-anim-1" style={{ fontSize: "clamp(48px, 8vw, 88px)", fontWeight: 800, lineHeight: 1.05, letterSpacing: isRTL ? "0" : "-0.04em", margin: "0 0 24px" }}>
            <span style={{ color: "#e2e8f0" }}>{t.hero.headline[0]}</span>
            <br />
            <span className="tt-gradient-text">{t.hero.headline[1]}</span>
          </h1>

          {/* Sub */}
          <p className="tt-anim tt-anim-2" style={{ fontSize: "clamp(16px, 2vw, 20px)", color: "#64748b", lineHeight: 1.6, marginBottom: 40, maxWidth: 520, margin: "0 auto 40px" }}>
            {t.hero.sub}
          </p>

          {/* CTAs */}
          <div className="tt-anim tt-anim-3" style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", alignItems: "center", marginBottom: 20 }}>
            <button className="tt-btn-primary" onClick={() => scrollTo("apps")}>
              <Download size={16} />
              {t.hero.cta}
            </button>
            <button
              onClick={() => scrollTo("pricing")}
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "#94a3b8", borderRadius: 9999, padding: "13px 28px", fontSize: 14, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", transition: "all 0.18s" }}
            >
              {t.nav.pricing}
            </button>
          </div>
          <p style={{ fontSize: 13, color: "#475569" }}>{t.hero.trial}</p>
        </div>

        {/* Scroll hint */}
        <div style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", opacity: 0.4, cursor: "pointer" }} onClick={() => scrollTo("apps")}>
          <ArrowDown size={20} color="#94a3b8" style={{ animation: "tt-fadeUp 1.5s ease-in-out infinite alternate" }} />
        </div>
      </section>

      {/* ═══ FEATURE PILLS ═══════════════════════════════════════════════════ */}
      <section style={{ padding: "0 24px 60px", display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", maxWidth: 800 }}>
          {t.features.map((f, i) => (
            <span key={i} className="tt-feature-pill">{f}</span>
          ))}
        </div>
      </section>

      {/* ═══ PLAYERS ════════════════════════════════════════════════════════ */}
      <section id="apps" style={{ padding: "80px 24px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <p className="tt-section-label">{t.hero.badge}</p>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#e2e8f0", margin: 0 }}>
            {isRTL ? "اختر مشغلك" : lang === "fi" ? "Valitse soittimesi" : lang === "fr" ? "Choisissez votre lecteur" : "Choose Your Player"}
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
          {PLAYERS_CONFIG.map((player) => {
            const pt = t.players[player.key];
            const Icon = player.icon;
            return (
              <div
                key={player.id}
                data-testid={`player-card-${player.id}`}
                className="tt-card"
                style={{ padding: 32, display: "flex", flexDirection: "column", gap: 20 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = player.accentBorder;
                  e.currentTarget.style.boxShadow = player.glow;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Icon */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ position: "relative", width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 52, height: 52, borderRadius: 14, background: player.accentDim, border: `1px solid ${player.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon size={24} color={player.accent} />
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: player.accent, background: player.accentDim, padding: "4px 10px", borderRadius: 6 }}>
                    APK
                  </span>
                </div>

                {/* Text */}
                <div>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: "#e2e8f0", margin: "0 0 4px", letterSpacing: "-0.02em" }}>
                    {pt.name}
                  </h3>
                  <p style={{ fontSize: 13, color: player.accent, fontWeight: 500, margin: "0 0 12px" }}>{pt.tagline}</p>
                  <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, margin: 0 }}>{pt.desc}</p>
                </div>

                {/* Download */}
                <a
                  href={player.apk}
                  download
                  data-testid={`download-${player.id}`}
                  className="tt-download-btn"
                  style={{ background: player.accentDim, border: `1px solid ${player.accentBorder}`, color: player.accent }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 4px 24px ${player.accentDim}`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                >
                  <Download size={15} />
                  {pt.btn}
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ PRICING ════════════════════════════════════════════════════════ */}
      <section id="pricing" style={{ padding: "80px 24px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <p className="tt-section-label">{t.pricing.title}</p>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#e2e8f0", margin: 0 }}>
            {t.pricing.sub}
          </h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
          {/* Trial card */}
          <div data-testid="pricing-trial" className="tt-card" style={{ padding: 36 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Clock size={18} color="#00e5ff" />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#00e5ff", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>{t.pricing.trial_title}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#e2e8f0", lineHeight: 1 }}>{t.pricing.trial_dur}</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.65, marginBottom: 24 }}>{t.pricing.trial_note}</p>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 10 }}>
              {t.pricing.trial_items.map((item, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#94a3b8" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(0,229,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Check size={10} color="#00e5ff" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <button className="tt-btn-primary" onClick={() => scrollTo("apps")} style={{ width: "100%", justifyContent: "center", fontSize: 14 }}>
              <Download size={14} />
              {isRTL ? "تحميل الآن" : lang === "fi" ? "Lataa nyt" : lang === "fr" ? "Télécharger" : "Download Now"}
            </button>
          </div>

          {/* License card */}
          <div
            data-testid="pricing-license"
            className="tt-card"
            style={{ padding: 36, borderColor: "rgba(168,85,247,0.3)", boxShadow: "0 0 40px rgba(168,85,247,0.08)", position: "relative", overflow: "hidden" }}
          >
            <div aria-hidden style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: 9999, padding: "3px 12px", marginBottom: 16, fontSize: 11, fontWeight: 600, color: "#a855f7", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              <Star size={10} fill="#a855f7" />
              {lang === "ar" ? "الأفضل قيمة" : lang === "fi" ? "Paras arvo" : lang === "fr" ? "Meilleure offre" : "Best Value"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Infinity size={18} color="#a855f7" />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#a855f7", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>{t.pricing.lic_title}</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: "#e2e8f0", lineHeight: 1 }}>{t.pricing.lic_price}</div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>{t.pricing.lic_once}</p>
            <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.65, marginBottom: 24 }}>{t.pricing.lic_note}</p>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 10 }}>
              {t.pricing.lic_items.map((item, i) => (
                <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "#94a3b8" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(168,85,247,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Check size={10} color="#a855f7" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=License Activation&body=Hi, I would like to purchase a permanent license. My device key is: `}
              data-testid="buy-license-btn"
              className="tt-download-btn"
              style={{ background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.35)", color: "#a855f7", textDecoration: "none", fontWeight: 700, fontSize: 14 }}
            >
              <Mail size={14} />
              {t.pricing.buy}
            </a>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══════════════════════════════════════════════════ */}
      <section style={{ padding: "80px 24px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#e2e8f0", margin: 0 }}>
            {t.steps.title}
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 32 }}>
          {t.steps.items.map((step, i) => (
            <div key={i} data-testid={`step-${i}`} style={{ position: "relative" }}>
              <div className="tt-step-num">{step.n}</div>
              <div style={{ width: 3, height: 24, background: "linear-gradient(to bottom, rgba(0,229,255,0.4), transparent)", margin: "8px 0 16px 2px" }} />
              <h3 style={{ fontSize: 17, fontWeight: 700, color: "#e2e8f0", margin: "0 0 10px", letterSpacing: "-0.01em" }}>{step.t}</h3>
              <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, margin: 0 }}>{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FAQ ════════════════════════════════════════════════════════════ */}
      <section id="faq" style={{ padding: "80px 24px", maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h2 style={{ fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#e2e8f0", margin: 0 }}>
            {t.faq.title}
          </h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {t.faq.items.map((item, i) => (
            <div
              key={i}
              data-testid={`faq-item-${i}`}
              className="tt-faq-item"
              style={{ borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, textAlign: isRTL ? "right" : "left", fontFamily: "inherit" }}
              >
                <span style={{ fontSize: 15, fontWeight: 600, color: openFaq === i ? "#00e5ff" : "#cbd5e1", flex: 1 }}>{item.q}</span>
                <div style={{ color: "#475569", flexShrink: 0, transition: "transform 0.2s", transform: openFaq === i ? "rotate(180deg)" : "none" }}>
                  <ChevronDown size={18} />
                </div>
              </button>
              {openFaq === i && (
                <div style={{ padding: "0 20px 18px", fontSize: 14, color: "#64748b", lineHeight: 1.7 }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CONTACT CTA ════════════════════════════════════════════════════ */}
      <section style={{ padding: "60px 24px 80px", maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
        <div style={{ background: "rgba(0,229,255,0.04)", border: "1px solid rgba(0,229,255,0.12)", borderRadius: 24, padding: "48px 32px" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <Mail size={22} color="#00e5ff" />
          </div>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: "#e2e8f0", margin: "0 0 12px" }}>
            {lang === "ar" ? "هل لديك سؤال؟" : lang === "fi" ? "Kysyttävää?" : lang === "fr" ? "Une question ?" : "Have a question?"}
          </h3>
          <p style={{ fontSize: 14, color: "#64748b", marginBottom: 24 }}>
            {lang === "ar" ? "تواصل معنا للحصول على المساعدة أو شراء ترخيص." : lang === "fi" ? "Ota yhteyttä saadaksesi apua tai ostaaksesi lisenssin." : lang === "fr" ? "Contactez-nous pour de l'aide ou pour acheter une licence." : "Reach out for support or to purchase a license."}
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-email"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600, color: "#00e5ff", textDecoration: "none", padding: "12px 28px", borderRadius: 12, border: "1px solid rgba(0,229,255,0.25)", background: "rgba(0,229,255,0.06)", transition: "all 0.18s" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,229,255,0.12)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,229,255,0.06)"; }}
          >
            <Mail size={16} />
            {CONTACT_EMAIL}
          </a>
        </div>
      </section>

      {/* ═══ FOOTER ═════════════════════════════════════════════════════════ */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "32px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg,#00e5ff,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Tv size={12} color="#000" />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#475569" }}>technotech</span>
          </div>
          <p style={{ fontSize: 13, color: "#334155", marginBottom: 8 }}>{t.footer.tagline}</p>
          <p style={{ fontSize: 12, color: "#1e293b" }}>
            © {new Date().getFullYear()} technotech · {t.footer.rights} · {t.footer.note}
          </p>
        </div>
      </footer>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .tt-lang-btn { padding: 4px 8px; font-size: 11px; }
          nav > div:nth-child(3) a { display: none; }
        }
        @media (max-width: 640px) {
          .tt-lang-btn span { display: none; }
        }
      `}</style>
    </div>
  );
}
